import { formatNumber, StatusPill, useTranslation } from '@hbs-us/ui';

import type { OverviewFleetAlerts } from '../overviewTypes.js';

import { HomeCard } from './HomeCard.js';

/**
 * Fleet alerts card (prod DashboardPage fleet-alerts block): the 30-day
 * registration / insurance expiring-soon + overdue counts with status labels.
 *
 * There is no empty state: a fleet with nothing expiring is four zeroes and four "Clear" pills,
 * which is the answer somebody came here for. Hiding it would make good news look like no data.
 */

function AlertTile({
  label,
  value,
  overdue,
  statusLabel,
  locale,
}: {
  label: string;
  value: number;
  overdue?: boolean;
  statusLabel: string;
  locale: string;
}) {
  const tone = value === 0 ? 'success' : overdue ? 'danger' : 'warning';
  return (
    <div className="rounded-lg border border-border bg-secondary/45 p-3">
      <div className="font-display text-3xl font-semibold tabular-nums text-foreground">
        {formatNumber(value, locale)}
      </div>
      <div className="mt-2 grid gap-2">
        <span className="text-muted-foreground text-xs">{label}</span>
        <StatusPill tone={tone} className="text-xs">
          {statusLabel}
        </StatusPill>
      </div>
    </div>
  );
}

export function FleetAlerts({ alerts }: { alerts: OverviewFleetAlerts }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return (
    <HomeCard headingId="home-fleet-heading" title={t('home:fleet.heading')}>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <AlertTile
          label={t('home:fleet.registrationExpiring')}
          value={alerts.registrationExpiringSoon}
          statusLabel={t(
            alerts.registrationExpiringSoon === 0 ? 'home:fleet.clear' : 'home:fleet.dueSoon',
          )}
          locale={locale}
        />
        <AlertTile
          label={t('home:fleet.registrationOverdue')}
          value={alerts.registrationOverdue}
          overdue
          statusLabel={t(
            alerts.registrationOverdue === 0 ? 'home:fleet.clear' : 'home:fleet.overdue',
          )}
          locale={locale}
        />
        <AlertTile
          label={t('home:fleet.insuranceExpiring')}
          value={alerts.insuranceExpiringSoon}
          statusLabel={t(
            alerts.insuranceExpiringSoon === 0 ? 'home:fleet.clear' : 'home:fleet.dueSoon',
          )}
          locale={locale}
        />
        <AlertTile
          label={t('home:fleet.insuranceOverdue')}
          value={alerts.insuranceOverdue}
          overdue
          statusLabel={t(alerts.insuranceOverdue === 0 ? 'home:fleet.clear' : 'home:fleet.overdue')}
          locale={locale}
        />
      </div>
    </HomeCard>
  );
}
